/**
 * The six attribution filters, in the order they render.
 *
 * One list drives the controls, the option lists and the request params, so the
 * v-model name, the dropdown it reads and the wire name cannot drift apart.
 * They did drift, on four screens at once, and every one of them failed
 * silently: the server reads its filters with `.get(key, None)` and
 * `.getlist(key)`, so a param under a name nothing looks for is not an error --
 * the request returns 200 with the table unfiltered, which looks exactly like a
 * filter that matched everything.
 *
 * What the four screens were sending, and what each cost:
 *
 *   LAS             bound `filters.source` / `filters.utm_media` but read
 *                   `filters.utm_source` / `filters.utm_medium` when building
 *                   the request -- undefined, so axios dropped both params.
 *   Pre-verification  bound `filters.utm_placement`, read `filters.placement`.
 *   Business Loan   read `this.utm_content` instead of `this.filters.utm_content`.
 *   New Customer    bound `filters.placement`.
 *
 * plus three of them sending UTM Platform as `placement`, which no list endpoint
 * has ever read. (cashpo-server keys.PLACEMENT exists, but only as a write-side
 * fallback when a signup payload carries attribution -- helper/attribution.py.)
 *
 * The names below are the ones every endpoint actually reads, in both shapes:
 * bracketed on the GET screens, because axios' default serializer writes an
 * array as `utm_source[]=a&utm_source[]=b` and the server reads it with
 * `getlist("utm_source[]")`; bare in the POST body on the customer_list screens,
 * where `as_filter_list` takes the JSON array as it stands.
 */
export const UTM_FILTERS = [
  { key: "utm_source", label: "Partner ID (utm_source)" },
  { key: "utm_medium", label: "Publisher (utm_medium)" },
  { key: "utm_campaign", label: "UTM Campaign" },
  { key: "utm_content", label: "Source (utm_content)" },
  { key: "utm_term", label: "UTM Term" },
  { key: "utm_platform", label: "UTM Platform" },
];

/** Just the keys, for a screen that only needs the names. */
export const UTM_KEYS = UTM_FILTERS.map((f) => f.key);

/**
 * The option list a filter key reads: always the key plus "s", which is how
 * users/get_utm_dropdown/ names its six lists.
 */
export function optionKey(key) {
  return key + "s";
}

/**
 * Only what is set, under the plain names the server reads.
 *
 * Empty selections are left out rather than sent as `[]`. That is not just
 * tidiness: precheck_index_queryset answers off the fast index only while every
 * non-empty body key is one it recognises, so sending noise would quietly push
 * those screens onto the slow path.
 */
export function utmParams(filters) {
  const out = {};
  UTM_FILTERS.forEach(({ key }) => {
    const value = filters && filters[key];
    if (value && value.length) out[key] = value;
  });
  return out;
}
